/* eslint-disable react/require-default-props */
import React from "react";
import { normalizeDialable } from "../../data/phoneNumbers";
import {
  Customer,
  CustomerPermissions,
  CustomerSort,
} from "../../services/CustomersService";

type CustomersTableProps = {
  customers: Customer[];
  loading: boolean;
  permissions: CustomerPermissions;
  sort: CustomerSort;
  order: "asc" | "desc";
  // eslint-disable-next-line no-unused-vars
  onSort: (sort: CustomerSort) => void;
  /** What an empty result means here, e.g. that a search matched nobody. */
  emptyMessage?: string;
};

const COLUMNS: { key: CustomerSort; label: string }[] = [
  { key: "user_id", label: "ID#" },
  { key: "last_name", label: "Apellido" },
  { key: "first_name", label: "Nombre" },
  { key: "date_came_in", label: "Fecha" },
  { key: "cell_phone", label: "Celular" },
  { key: "house_phone", label: "Casa" },
  { key: "status", label: "Estatus" },
];

// Light and dark like the home page's cards; the table sits inside one.
// px-3.5/py-3.5, not px-4/py-3: Bootstrap 4 has px-4 (1.5rem !important) and
// py-3, which widened every cell until the five-link Accounting row clipped at
// the card edge. Half steps have no Bootstrap rule, so Tailwind's value holds.
const headerClasses =
  "px-3.5 py-3.5 text-left text-xs font-medium uppercase tracking-wide whitespace-nowrap " +
  "text-gray-500 dark:text-gray-400";
const sortButtonClasses =
  "inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-900 dark:hover:text-gray-100";
const activeSortClasses = "text-blue-700 dark:text-blue-400";
const cellClasses =
  "px-3.5 py-3.5 text-sm whitespace-nowrap text-gray-700 dark:text-gray-300";
const strongCellClasses =
  "px-3.5 py-3.5 text-sm whitespace-nowrap font-medium text-gray-900 dark:text-gray-100";
const actionClasses =
  "font-medium text-blue-700 hover:underline dark:text-blue-400";
const statusClasses =
  "inline-block rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium " +
  "text-gray-700 dark:bg-slate-700 dark:text-gray-200";
const messageClasses =
  "py-12 text-center text-sm text-gray-500 dark:text-gray-400";

/** `date_came_in` is `NOW()` stored as text; the old page printed MM-DD-YYYY. */
export function formatCustomerDate(value: string | null): string {
  if (!value) return "";

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);

  return match ? `${match[2]}-${match[3]}-${match[1]}` : value;
}

/**
 * A number the softphone can dial carries data-call-number, already
 * normalised, and useClickToCall's delegated listener does the rest -- the
 * table and the embedded softphone share that DOM contract rather than
 * passing callbacks, so the table works with or without a softphone. Anything else is plain text: a
 * cell that cannot place a call must never look like it can.
 */
function phoneCell(value: string | null): React.ReactNode {
  const raw = (value ?? "").trim();

  if (raw === "") return null;

  const dialable = normalizeDialable(raw);

  if (dialable === null) return raw;

  return <span data-call-number={dialable}>{raw}</span>;
}

function ariaSort(
  active: boolean,
  order: "asc" | "desc",
): "ascending" | "descending" | "none" {
  if (!active) return "none";

  return order === "asc" ? "ascending" : "descending";
}

export default function CustomersTable({
  customers,
  loading,
  permissions,
  sort,
  order,
  onSort,
  emptyMessage = "No hay ningún paciente registrado.",
}: CustomersTableProps) {
  // Only the first load blanks the table. Paging keeps the current rows,
  // dimmed, so the headers stay put and the page does not jump.
  if (loading && customers.length === 0) {
    return <p className={messageClasses}>Cargando…</p>;
  }

  if (customers.length === 0) {
    return <p className={messageClasses}>{emptyMessage}</p>;
  }

  return (
    <div
      className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}
      aria-busy={loading}
    >
      <table className="min-w-full">
        <thead className="bg-gray-50 dark:bg-slate-900/40">
          <tr>
            {COLUMNS.map((column) => {
              const active = column.key === sort;

              return (
                <th
                  key={column.key}
                  className={headerClasses}
                  aria-sort={ariaSort(active, order)}
                >
                  <button
                    type="button"
                    className={`${sortButtonClasses} ${
                      active ? activeSortClasses : ""
                    }`}
                    onClick={() => onSort(column.key)}
                  >
                    {column.label}
                    {active ? (
                      <span aria-hidden="true">
                        {order === "asc" ? "▲" : "▼"}
                      </span>
                    ) : null}
                  </button>
                </th>
              );
            })}
            <th className={headerClasses}>
              <span className="sr-only">Acciones</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {customers.map((customer) => (
            <tr
              key={customer.user_id}
              className="border-t border-gray-100 hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-700/40"
            >
              <td className={strongCellClasses}>{customer.user_id}</td>
              <td className={strongCellClasses}>{customer.last_name}</td>
              <td className={cellClasses}>{customer.first_name}</td>
              <td className={cellClasses}>
                {formatCustomerDate(customer.date_came_in)}
              </td>
              <td className={cellClasses}>{phoneCell(customer.cell_phone)}</td>
              <td className={cellClasses}>{phoneCell(customer.house_phone)}</td>
              <td className={cellClasses}>
                {customer.status ? (
                  <span className={statusClasses}>{customer.status}</span>
                ) : null}
              </td>
              <td className={cellClasses}>
                <div className="flex gap-3">
                  <a
                    className={actionClasses}
                    href={`show_orders1.php?id=${customer.user_id}`}
                  >
                    Ordenes
                  </a>
                  {permissions.can_invoice ? (
                    <a
                      className={actionClasses}
                      href={`show_invoices.php?id=${customer.user_id}`}
                    >
                      Invoice
                    </a>
                  ) : null}
                  {permissions.can_create_order ? (
                    <a
                      className={actionClasses}
                      href={`create_orders1.php?id=${customer.user_id}`}
                    >
                      Crear
                    </a>
                  ) : null}
                  {permissions.can_edit ? (
                    <a
                      className={actionClasses}
                      href={`edit_customer1.php?id=${customer.user_id}`}
                    >
                      Editar
                    </a>
                  ) : null}
                  {permissions.can_delete ? (
                    <a
                      className={actionClasses}
                      href={`delete_customer.php?id=${customer.user_id}`}
                    >
                      Borrar
                    </a>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
